import chunk from 'lodash/chunk';
import moment from 'moment';
import { getAddressById, userApi } from '@atheneum/api';
import { uuid } from 'uuidv4';
import { sendMetric } from '@atheneum/api/dist/lib';
export const formatZoomMeetingId = zoomMeetingId => {
  const zoomMeetingIdNumberArray = Array.from(zoomMeetingId + '');
  const zoomMeetingNumberGroups = chunk(zoomMeetingIdNumberArray, 3);
  const resp = zoomMeetingNumberGroups.map(group => group.join('')).join('-');
  return resp;
};

const renderPhoneNumbers = (staticData, zoomNumber, zoomMeetingId, zoomMeetingPassword) => {
  const noNumbersFoundMessage = 'No local numbers found, please find a number on the link below';

  if (!zoomNumber) {
    return noNumbersFoundMessage;
  }

  const {
    internationalNumbers,
    tollFreeNumbers,
    countryId
  } = zoomNumber;
  const countryName = staticData.names.countries[countryId];
  const internationalNums = internationalNumbers.split(',').map((num, i) => {
    const numNoSpaces = num.split(' ').join('');
    const formattedNum = numNoSpaces.split('(')[0];
    return `${countryName} toll number ${i += 1}: <a href="tel:${formattedNum},,${zoomMeetingId}#,,,,,,#,,,${zoomMeetingPassword}#">1-Tap</a> ${num}`;
  }).join('<br/>');

  if (internationalNums) {
    return internationalNums;
  }

  const tollFreeNums = tollFreeNumbers.split(',').map((num, i) => `${countryName} Toll Free number ${i += 1}: ${num}`).join('<br />');

  if (tollFreeNums) {
    return tollFreeNums;
  }
};

export const generateZoomInfo = async ({
  staticData,
  schedule,
  password,
  clientContacts,
  allZoomNumbers
}) => {
  let clientCountryIds = await Promise.all(clientContacts.map(async contact => {
    let addressId;

    if (!contact.addressId) {
      const result = await userApi.getUserAddressByEmail(contact.email);
      addressId = result.addressId;
    } else {
      addressId = contact.addressId;
    }

    const {
      countryId: clientCountryId
    } = await getAddressById(addressId);
    return clientCountryId;
  }));
  let clientZoomNums = clientCountryIds.map(id => allZoomNumbers.find(zoomData => zoomData.countryId === id)).map(o => ({ // Note: is .countryId guaranteed to be present for o?
    ...o,
    countryName: staticData.names.countries[o.countryId]
  }));
  if (!schedule) return '';
  const zoomMeetingId = schedule.zoomMeetingId ? schedule.zoomMeetingId : schedule.zoom_meeting_id;
  const zoomJoinUrl = schedule.zoomJoinUrl ? schedule.zoomJoinUrl : schedule.zoom_join_url;
  let zoomInfo = '';
  zoomInfo += `
    <p>
      If you would like to connect via Zoom, follow these links: <br/>
      <div id="clientJoinUrls">CLIENT_JOIN_URLS</div>
    </p>

    <p>
      Enter the following Meeting ID <b>${formatZoomMeetingId(zoomMeetingId)}#</b> <br/>
      Meeting password is: <b>${password}</b>
    </p>

    <p>
      You can check all the available dial in numbers <a href="https://atheneum-partners.zoom.us/u/ab6dpgC1N3">here</a>
    </p>
  `;
  clientZoomNums.forEach(zoomNumber => {
    zoomInfo += `
      <p>
        One-tap dial in for iPhones: <br/>
        ${renderPhoneNumbers(staticData, zoomNumber, zoomMeetingId, password)}
      <p/>
      <p>
        Local number(s) to dial in via regular phone: <br/>
        ${zoomNumber.countryName} ${!!zoomNumber.internationalNumbers ? 'Toll' : 'Toll free'} ${zoomNumber.internationalNumbers.replace(',', ' or ')}
      </p>
    `;
  });
  return zoomInfo;
};
export const callRecordedText = ({
  schedule
}) => {
  if (!schedule) return '';
  let callText = '';

  if (schedule.callAutoRecording) {
    callText = '<p>Please note that the call is set to be automatically recorded. Should you wish to not record this call, please let us know.</p>';
  }

  return callText;
};
export const extractEmails = additionalEmails => {
  if (!additionalEmails) return [];
  return additionalEmails.map(data => {
    return {
      email: data.email
    };
  });
};

const isNew = ({
  currentPath,
  readOnly,
  schedule,
  dayToRender,
  duration,
  availabilityStore,
  opsTime
}) => {
  var _schedule$timeslots$, _schedule$timeslots$$;

  if (currentPath === 'overview' || !readOnly) {
    return false;
  }

  const oldStartTimestamp = new Date(availabilityStore.schedule.startTime).getTime();
  const newStartTimestamp = parseInt((_schedule$timeslots$ = schedule.timeslots[0]) === null || _schedule$timeslots$ === void 0 ? void 0 : (_schedule$timeslots$$ = _schedule$timeslots$.time) === null || _schedule$timeslots$$ === void 0 ? void 0 : _schedule$timeslots$$.start);
  const {
    time: {
      start
    }
  } = getNewTimeSlot(new Date(dayToRender.startOf('day')), opsTime, duration);
  return !!schedule.timeslots.length && newStartTimestamp !== parseInt(start) || !!availabilityStore.schedule && oldStartTimestamp !== newStartTimestamp;
};

export const isExpertRescheduled = ({
  currentPath,
  readOnly,
  schedule,
  dayToRender,
  duration,
  availabilityStore,
  quickUpdateExpert,
  expertinviteEdited,
  expertInvited,
  expertAdditionalsAdded,
  opsTime
}) => {
  // What is not rescheduled
  // If there's no existing schedule, then it's defintely not reschedule
  if (!availabilityStore.schedule) return false; // If new expert is added, it's treated as new schedule

  if (expertAdditionalsAdded) return false; // What is rescheduled

  if (quickUpdateExpert && expertInvited) return true;
  if (expertinviteEdited === 'expert' && expertInvited) return true;
  const didTimeChange = isNew({
    currentPath,
    readOnly,
    schedule,
    dayToRender,
    duration,
    availabilityStore,
    opsTime
  });
  if (didTimeChange && expertInvited) return true; // Default to new schedule

  return false;
};
export const isClientRescheduled = ({
  currentPath,
  readOnly,
  schedule,
  dayToRender,
  duration,
  availabilityStore,
  quickUpdateClient,
  clientinviteEdited,
  clientInvited,
  clientsAdded,
  opsTime
}) => {
  // What is not rescheduled
  // If there's no existing schedule, then it's defintely not reschedule
  if (!availabilityStore.schedule) return false; // If new expert is added, it's treated as new schedule

  if (clientsAdded) return false; // What is rescheduled

  if (quickUpdateClient && clientInvited) return true;
  if (clientinviteEdited === 'client' && clientInvited) return true;
  const didTimeChange = isNew({
    currentPath,
    readOnly,
    schedule,
    dayToRender,
    duration,
    availabilityStore,
    opsTime
  });
  if (didTimeChange && clientInvited) return true; // Default to new schedule

  return false;
}; // Data validation before submitting data for schedule
// Return boolean

export const isDataValid = ({
  currentPath,
  readOnly,
  schedule,
  dayToRender,
  duration,
  availabilityStore,
  quickUpdateExpert,
  expertinviteEdited,
  quickUpdateClient,
  clientinviteEdited,
  expertsAdded,
  growlStore,
  clientContacts,
  multiAction,
  connectedExperts,
  takenExpertSlots,
  opsTime,
  selectedTeam,
  onQuick,
  onCustom
}) => {
  // If team is not assigned, don't proceed further except for zoom meeting
  if ((onQuick || onCustom) && !(selectedTeam && selectedTeam.label)) {
    sendMetric('scheduling.MissingTeam', 'scheduling', {
      status: 'error'
    });
    growlStore.showError('A team must be selected to continue.', 'ERROR!');
    return false;
  }

  if (!expertsAdded.length && !availabilityStore.epl.expert.email && readOnly && (quickUpdateExpert || expertinviteEdited === 'expert')) {
    sendMetric('scheduling.MissingEmail', 'scheduling', {
      status: 'error'
    });
    growlStore.showError('Expert email address is missing. Please complete it', 'ERROR!');
    return false;
  }

  if (!expertsAdded.length && !!clientContacts && !!clientContacts[0] && clientContacts.some(contact => !contact.email) && readOnly && (quickUpdateClient || clientinviteEdited === 'client')) {
    sendMetric('scheduling.EmailMissing', 'scheduling', {
      status: 'error'
    });
    growlStore.showError('Client email address is missing. Please complete it', 'ERROR!');
    return false;
  }

  if (!multiAction && !!connectedExperts && !!connectedExperts.length && currentPath === 'calendarview' && readOnly) {
    growlStore.showError(`
      This schedule is sharing Zoom meeting with other experts, so it cant be updated. <br/>
      To reschedule this call:<br/>
      1. remove existing schedule and confirm <br/>
      2. then create new schedule and Zoom meeting <br/>
      <strong>Or</strong><br/>
      Reschedule all EPLs in the meeting at once by clicking on the three dots in the top right corner of the multi Expert container
    `);
    sendMetric('scheduling.LongErrorMultipleExperts', 'scheduling', {
      status: 'error'
    });
    return false;
  }

  if (availabilityStore.epl.eplStatusId < availabilityStore.staticData.CONST.EPL_STATUSES.SUBMITTED && (!!schedule.timeslots.length || readOnly)) {
    growlStore.showError('Cannot Schedule EPL in status lower than Submitted!', 'ERROR!');
    return false;
  }

  if (!!schedule.timeslots.length && readOnly) {
    const timeNow = parseInt(moment().format('x'));
    const timePicked = currentPath === 'overview' && !!schedule.timeslots.length ? schedule.timeslots[0].time.start : parseInt(getNewTimeSlot(new Date(dayToRender.startOf('day')), opsTime, duration).time.start);

    if (timePicked < timeNow) {
      growlStore.showError('Select a timeslot past the current time', `Error: Cannot schedule in the past!`);
      sendMetric('scheduling.ScheduleInPast', 'scheduling', {
        status: 'error'
      });
      return false;
    }
  } // Check if timeslot is already taken


  if (currentPath === 'calendarview' && readOnly) {
    const startTimes = takenExpertSlots.map(slot => parseInt(moment(slot.startTime).format('x')));
    const endTimes = takenExpertSlots.map(slot => parseInt(moment(slot.endTime).format('x')));
    const start = getNewTimeSlot(new Date(dayToRender.startOf('day')), opsTime, duration).time.start;
    const end = getNewTimeSlot(new Date(dayToRender.startOf('day')), opsTime, duration).time.end;
    const startTimeCheck = startTimes.every(startTime => {
      return start <= startTime && end > startTime;
    });
    const endTimesCheck = endTimes.every(endTime => {
      return start < endTime && end >= endTime;
    });
    const freeSlot = !startTimeCheck && !endTimesCheck;

    if (takenExpertSlots.length && !freeSlot) {
      growlStore.showError('Slot is already taken for this expert.');
      sendMetric('scheduling.SlotTaken', 'scheduling', {
        status: 'error'
      });
      return false;
    }

    const timeNow = parseInt(moment().format('x'));
    const timePicked = parseInt(getNewTimeSlot(new Date(dayToRender.startOf('day')), opsTime, duration).time.start);

    if (timePicked < timeNow) {
      growlStore.showError('Select a timeslot past the current time', `Error: Cannot schedule in the past!`);
      sendMetric('scheduling.ScheduleInPast', 'scheduling', {
        status: 'error'
      });
      return false;
    }
  } // If the code get to here, then data is valid


  return true;
};
export const getNewTimeSlot = (selectedStartDate, selectedStartTime, duration) => {
  let finalHour = selectedStartDate.getHours();
  let finalMinutes = selectedStartDate.getMinutes();

  if (selectedStartTime) {
    const {
      hour,
      minutes
    } = selectedStartTime;
    finalHour = hour;
    finalMinutes = minutes;
  }

  const newTimeslot = {
    duration: duration,
    id: uuid(),
    time: {
      start: selectedStartDate.getTime() + 1000 * 3600 * finalHour + 1000 * 60 * finalMinutes,
      end: selectedStartDate.getTime() + 1000 * 3600 * finalHour + 1000 * 60 * finalMinutes + 1000 * 60 * duration
    },
    drafts: []
  };
  return newTimeslot;
};